/**
 * The desk between the browser and the app.
 *
 * When someone signs in with Google from one of the desktop apps, the browser comes
 * here rather than to a custom-scheme URL, and this page hands the result to the app.
 * The apps ask for exactly this: `redirect_to` is an ordinary `https` address on a
 * domain we serve, because a browser asked to navigate to `orobasagent://…` has to
 * guess whether that is a page, a search or an application request — and each answers
 * differently. An address we control behaves the same everywhere.
 *
 * ## What it carries
 *
 * GoTrue redirects here with `code` (and `nonce`, which it was given inside the very
 * same `redirect_to`) and this page forwards both, unchanged, to the app's own scheme.
 * The app then exchanges the code for tokens against a PKCE verifier that never left
 * it, so the code is useless to anybody who reads this page's address bar. This page
 * sees a code once and cannot do anything with it.
 *
 * ## Why it is a relay and not a sign-in
 *
 * Signing in *here* would mint a session for this browser, which is not what the person
 * asked for — they pressed a button in an app. The two sessions are the same account
 * but they are not the same thing, and quietly signing the browser in as a side effect
 * of signing an app in is a surprise that shows up later as "why is this laptop signed
 * in".
 *
 * ## Why the app list is closed
 *
 * `app` arrives from a query string. It is matched against the two names below and
 * anything else is refused. Building the hand-off URL by pasting that value into a
 * string would turn this page into an open redirect that carries an authorisation code
 * to wherever the query asked for — which is the one thing this page must never be.
 *
 * ## Why this returns no prose
 *
 * The two refusals below used to be finished sentences, which was wrong twice over.
 * They were resolved during render, before `setActiveDictionary` had run, so a cold
 * load of `/en/auth/callback` painted a Portuguese refusal under an English heading.
 * And the value was frozen in a `useMemo` keyed on nothing, so switching language on
 * a page that was already open left the old language's sentence sitting under the new
 * language's heading.
 *
 * So this returns which refusal it was, and `DesktopCallback.tsx` picks the sentence
 * at render time from the dictionary in scope. A provider's own `error_description`
 * is still passed through untouched: when GoTrue says something this file does not
 * recognise, its wording reaches the reader rather than a guess.
 */

export type CallbackRefusal = 'cancelled' | 'provider';

/** The two desktop apps, and the scheme each one registers for its hand-off. */
export const DESKTOP_APPS = [
  { id: 'orobasagent', name: 'OrobasHipercube', scheme: 'orobasagent' },
  { id: 'orobaswork', name: 'Orobas Work', scheme: 'orobaswork' },
] as const;

export type DesktopApp = (typeof DESKTOP_APPS)[number];

export function desktopAppFor(id: string | null): DesktopApp | null {
  if (id === null) return null;
  // A closed lookup, not a parse. `id` comes straight off the query string and the
  // only thing it decides here is which of two known schemes gets opened.
  return DESKTOP_APPS.find((app) => app.id === id) ?? null;
}

/** Query keys. Named here because both the apps and this page have to agree on them. */
export const OAUTH_APP_KEY = 'app';
export const OAUTH_NONCE_KEY = 'nonce';

/**
 * The URL that hands a finished sign-in to `app`.
 *
 * Built from parts this file owns rather than from the incoming query, so the only
 * thing the query contributes is *which* app and the two values to carry. `code` and
 * `nonce` are re-encoded through `URLSearchParams`, which is what stops a value
 * containing `&` or `=` from splitting into extra parameters on the way.
 */
export function desktopHandOffUrl(
  app: DesktopApp,
  params: { readonly code: string; readonly nonce: string },
): string {
  const query = new URLSearchParams({ code: params.code, [OAUTH_NONCE_KEY]: params.nonce });
  return `${app.scheme}://auth/callback?${query.toString()}`;
}

export type Outcome =
  | { readonly kind: 'handing-off'; readonly app: DesktopApp; readonly url: string }
  | {
      readonly kind: 'declined';
      readonly refusal: CallbackRefusal;
      /** GoTrue's own words, when it gave any. Never replaced by a guess. */
      readonly providerReason: string | null;
    }
  | { readonly kind: 'unknown-app' }
  | { readonly kind: 'incomplete'; readonly missing: readonly string[] };

/**
 * Works out what this page should do with the request it was given.
 *
 * Separate from the component because the decision is the part worth testing and the
 * part worth reasoning about; rendering is not. It reads a `URLSearchParams` rather than
 * touching `window`, so it can be exercised without a browser.
 */
export function readCallback(params: URLSearchParams): Outcome {
  const app = desktopAppFor(params.get(OAUTH_APP_KEY));

  // The provider refused — the reader said no, or Google did. Nothing to hand over, and
  // the app is told so by its own screen when nothing arrives, so the useful thing here
  // is to say what happened rather than to open an app with an empty callback.
  const error = params.get('error');
  if (error !== null && error !== '') {
    const description = params.get('error_description');
    const refusedByTheReader = error === 'access_denied';
    return {
      kind: 'declined',
      refusal: refusedByTheReader ? 'cancelled' : 'provider',
      // An empty `error_description` is not a description. Passing `''` through would
      // render a blank paragraph under the heading.
      providerReason: refusedByTheReader || description == null || description === '' ? null : description,
    };
  }

  if (app === null) return { kind: 'unknown-app' };

  const code = params.get('code');
  const nonce = params.get(OAUTH_NONCE_KEY);
  const missing: string[] = [];
  if (code === null || code === '') missing.push('code');
  if (nonce === null || nonce === '') missing.push('nonce');
  if (missing.length > 0) return { kind: 'incomplete', missing };

  // Both present and non-empty: the only case that produces an app URL.
  return { kind: 'handing-off', app, url: desktopHandOffUrl(app, { code: code!, nonce: nonce! }) };
}